import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, Field, KpiTile, KpiGrid, BackLink } from "@/components/common/Page";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { CompanyMark } from "@/components/common/CompanyMark";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { ActivityFeed, AuditTrail } from "@/components/common/ActivityFeed";
import { CustomerFormSheet } from "./CustomersPage";
import { LeadCreateSheet } from "@/pages/leads/LeadsPage";
import { nok, fmtDate } from "@/lib/format";
import { label } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Pencil, Plus, Trash2, Star, Phone, Mail, Inbox, FolderKanban, FileText, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const key = ["customer", id];
  const { data: c, isLoading, error, refetch } = useQuery({ queryKey: key, queryFn: () => get(`/customers/${id}`) });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: () => get("/users"), enabled: can("users:read") });
  const [tab, setTab] = useState("oversikt");
  const [editOpen, setEditOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["customers"] }); };
  const removeContact = useMutation({ mutationFn: (cid) => del(`/contacts/${cid}`), onSuccess: () => { toast.success("Kontakt slettet"); invalidate(); }, onError: (e) => toastError(e) });
  const remove = useMutation({ mutationFn: () => del(`/customers/${id}`), onSuccess: () => { toast.success("Kunde slettet"); navigate("/kunder"); }, onError: (e) => toastError(e) });

  if (isLoading) return <LoadingState className="py-32" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const openLeads = (c.leads || []).filter((l) => !["vunnet", "tapt"].includes(l.stage));
  const activeProjects = (c.projects || []).filter((p) => p.status !== "avsluttet");
  const quoteValue = (c.quotes || []).filter((q) => q.status === "akseptert").reduce((s, q) => s + (q.total || 0), 0);

  return (
    <div className="page-pad space-y-4 fade-up" data-testid="customer-detail-page">
      <BackLink to="/kunder" testId="customer-back-link">Kunder</BackLink>
      <PageHeader
        leading={<CompanyMark kind="customer" id={c.id} src={c.logo_url || undefined} name={c.name} size="xl" testId="customer-detail-mark" />}
        title={c.name}
        subtitle={<span className="flex flex-wrap gap-2 items-center">{c.org_number && <span className="font-mono">Org.nr {c.org_number}</span>}{c.industry && <span>· {c.industry}</span>}{c.city && <span>· {c.city}</span>}{c.price_agreement && <span className="rounded bg-muted px-1.5 py-0.5 text-[11.5px] text-foreground/80">{c.price_agreement}</span>}</span>}
        actions={
          <>
            {can("leads:write") && <Button variant="outline" onClick={() => setLeadOpen(true)} data-testid="customer-new-lead-button"><Inbox /> Ny henvendelse</Button>}
            {can("customers:write") && <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="customer-edit-button"><Pencil /> Rediger</Button>}
          </>
        }
      />
      <KpiGrid>
        <KpiTile label="Aktive prosjekter" value={activeProjects.length} hint={`${(c.projects || []).length} totalt`} onClick={() => setTab("prosjekter")} testId="kpi-tile-customer-projects" />
        <KpiTile label="Åpne henvendelser" value={openLeads.length} hint={`${(c.leads || []).length} totalt`} onClick={() => setTab("henvendelser")} testId="kpi-tile-customer-leads" />
        <KpiTile label="Tilbud" value={(c.quotes || []).length} hint={`Akseptert verdi ${nok(quoteValue)}`} onClick={() => setTab("tilbud")} testId="kpi-tile-customer-quotes" />
        <KpiTile label="Kontakter" value={(c.contacts || []).length} hint={c.account_manager_name ? `Kundeansvarlig: ${c.account_manager_name}` : "Ingen kundeansvarlig"} onClick={() => setTab("oversikt")} testId="kpi-tile-customer-contacts" />
      </KpiGrid>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap" data-testid="customer-detail-tabs">
          <TabsTrigger value="oversikt" data-testid="customer-tab-oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="henvendelser" data-testid="customer-tab-henvendelser">Henvendelser ({(c.leads || []).length})</TabsTrigger>
          <TabsTrigger value="prosjekter" data-testid="customer-tab-prosjekter">Prosjekter ({(c.projects || []).length})</TabsTrigger>
          <TabsTrigger value="tilbud" data-testid="customer-tab-tilbud">Tilbud ({(c.quotes || []).length})</TabsTrigger>
          <TabsTrigger value="dokumenter" data-testid="customer-tab-dokumenter">Dokumenter {c.document_count ? `(${c.document_count})` : ""}</TabsTrigger>
          <TabsTrigger value="aktivitet" data-testid="customer-tab-aktivitet">Aktivitet</TabsTrigger>
          <TabsTrigger value="historikk" data-testid="customer-tab-historikk">Historikk</TabsTrigger>
        </TabsList>

        <TabsContent value="oversikt" className="mt-4 grid lg:grid-cols-3 gap-4">
          <Section title="Kontaktpersoner" className="lg:col-span-2" actions={can("customers:write") && <Button size="sm" variant="outline" onClick={() => { setEditContact(null); setContactOpen(true); }} data-testid="contact-add-button"><Plus /> Kontakt</Button>}>
            {!c.contacts?.length ? (
              <EmptyState title="Ingen kontaktpersoner" icon={Users} className="py-8" testId="contacts-empty" />
            ) : (
              <ul className="divide-y rounded-md border" data-testid="contacts-list">
                {c.contacts.map((ct) => (
                  <li key={ct.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-[13px]" data-testid={`contact-row-${ct.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium flex items-center gap-1.5">{ct.name} {ct.is_primary && <Star className="h-3 w-3 fill-foreground/60 text-foreground/60" aria-label="Hovedkontakt" />}</div>
                      <div className="text-xs text-muted-foreground">{ct.title || "–"}</div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {ct.phone && <a href={`tel:${ct.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="h-3.5 w-3.5" /> {ct.phone}</a>}
                      {ct.email && <a href={`mailto:${ct.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="h-3.5 w-3.5" /> {ct.email}</a>}
                    </div>
                    {can("customers:write") && (
                      <div className="flex gap-1">
                        <button className="text-muted-foreground hover:text-foreground" onClick={() => { setEditContact(ct); setContactOpen(true); }} aria-label="Rediger" data-testid={`contact-edit-${ct.id}`}><Pencil className="h-4 w-4" /></button>
                        <button className="text-muted-foreground hover:text-destructive" onClick={() => removeContact.mutate(ct.id)} aria-label="Slett" data-testid={`contact-delete-${ct.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {c.notes && <div className="mt-4"><Field label="Notater"><p className="whitespace-pre-wrap">{c.notes}</p></Field></div>}
          </Section>
          <div className="space-y-4">
            <Section title="Firmainformasjon">
              <div className="space-y-3">
                <Field label="Besøksadresse" value={[c.address, [c.postal_code, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")} />
                <Field label="Nettside">{c.website ? <a className="hover:underline" href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer">{c.website}</a> : "–"}</Field>
                <Field label="Kundeansvarlig" value={c.account_manager_name} />
                <Field label="Kunde siden" value={fmtDate(c.created_at)} />
              </div>
            </Section>
            <Section title="Kommersielle betingelser">
              <div className="space-y-3">
                <Field label="Avtalt rabatt" value={c.discount_pct ? `${c.discount_pct} %` : "Ingen"} mono />
                <Field label="Prisavtale" value={c.price_agreement} />
                <Field label="Betalingsbetingelser" value={c.payment_terms_days ? `${c.payment_terms_days} dager` : null} />
                <Field label="Faktura e-post" value={c.invoice_email} />
                <Field label="Fakturaadresse" value={c.invoice_address} />
                <Field label="Fakturareferanse" value={c.invoice_reference} />
              </div>
            </Section>
            {can("customers:write") && (c.projects || []).length === 0 && (
              <Button variant="ghost" className="w-full justify-start text-destructive hover:text-destructive" onClick={() => window.confirm("Slette kunden?") && remove.mutate()} data-testid="customer-delete-button"><Trash2 /> Slett kunde</Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="henvendelser" className="mt-4">
          <Section title="Henvendelser" actions={can("leads:write") && <Button size="sm" onClick={() => setLeadOpen(true)} data-testid="customer-new-lead-button-2"><Plus /> Ny henvendelse</Button>}>
            {!c.leads?.length ? <EmptyState title="Ingen henvendelser" icon={Inbox} className="py-8" /> : (
              <ul className="divide-y rounded-md border">
                {c.leads.map((l) => (
                  <li key={l.id} className="flex cursor-pointer flex-wrap items-center gap-3 px-3 py-2 text-[13px] hover:bg-muted/50" onClick={() => navigate(`/henvendelser/${l.id}`)} data-testid={`customer-lead-row-${l.id}`}>
                    <span className="font-mono text-xs text-muted-foreground">{l.lead_number}</span>
                    <span className="flex-1 font-medium">{l.title}</span>
                    <StatusBadge status={l.stage} entity="lead" />
                    <span className="text-xs text-muted-foreground tabular">{label(l.need_type)}{l.persons ? ` · ${l.persons} pers` : ""} · {fmtDate(l.desired_start)}</span>
                    <span className="font-mono text-sm tabular w-28 text-right">{nok(l.estimated_value)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="prosjekter" className="mt-4">
          <Section title="Prosjekter">
            {!c.projects?.length ? <EmptyState title="Ingen prosjekter" icon={FolderKanban} className="py-8" /> : (
              <ul className="divide-y rounded-md border">
                {c.projects.map((p) => (
                  <li key={p.id} className="flex cursor-pointer flex-wrap items-center gap-3 px-3 py-2 text-[13px] hover:bg-muted/50" onClick={() => navigate(`/prosjekter/${p.id}`)} data-testid={`customer-project-row-${p.id}`}>
                    <span className="font-mono text-xs text-muted-foreground">{p.project_number}</span>
                    <span className="flex-1 font-medium">{p.name}</span>
                    <StatusBadge status={p.status} entity="project" />
                    <span className="text-xs text-muted-foreground">{p.address}</span>
                    <span className="text-xs tabular">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="tilbud" className="mt-4">
          <Section title="Tilbud">
            {!c.quotes?.length ? <EmptyState title="Ingen tilbud" icon={FileText} className="py-8" /> : (
              <ul className="divide-y rounded-md border">
                {c.quotes.map((q) => (
                  <li key={q.id} className="flex cursor-pointer flex-wrap items-center gap-3 px-3 py-2 text-[13px] hover:bg-muted/50" onClick={() => navigate(`/tilbud/${q.id}`)} data-testid={`customer-quote-row-${q.id}`}>
                    <span className="font-mono text-xs">{q.quote_number} v{q.current_version}</span>
                    <span className="flex-1">{q.title}</span>
                    <StatusBadge status={q.status} entity="quote" />
                    <span className="text-xs text-muted-foreground">gyldig til {fmtDate(q.valid_until)}</span>
                    <span className="font-mono tabular w-32 text-right">{nok(q.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="dokumenter" className="mt-4"><Section title="Dokumenter" description="Rammeavtaler, kontrakter og annen kundedokumentasjon"><DocumentsPanel entityType="customer" entityId={id} defaultCategory="kontrakt" /></Section></TabsContent>
        <TabsContent value="aktivitet" className="mt-4"><Section title="Aktivitet" description="Samlet kommunikasjon på kunden, inkludert henvendelser og prosjekter"><ActivityFeed entityType="customer" entityId={id} /></Section></TabsContent>
        <TabsContent value="historikk" className="mt-4"><Section title="Endringslogg"><AuditTrail entityType="customer" entityId={id} /></Section></TabsContent>
      </Tabs>

      {editOpen && <CustomerFormSheet open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={invalidate} />}
      <LeadCreateSheet open={leadOpen} onOpenChange={setLeadOpen} users={users} defaultOwner={user?.id} customerId={id} customerName={c.name} />
      {contactOpen && <ContactSheet open={contactOpen} onOpenChange={setContactOpen} customerId={id} contact={editContact} onSaved={invalidate} />}
    </div>
  );
}

const ContactSheet = ({ open, onOpenChange, customerId, contact, onSaved }) => {
  const [f, setF] = useState(contact ? { name: contact.name, title: contact.title || "", email: contact.email || "", phone: contact.phone || "", is_primary: !!contact.is_primary, notes: contact.notes || "" } : { name: "", title: "", email: "", phone: "", is_primary: false, notes: "" });
  const save = useMutation({
    mutationFn: () => (contact ? patch(`/contacts/${contact.id}`, f) : post(`/customers/${customerId}/contacts`, f)),
    onSuccess: () => { toast.success(contact ? "Kontakt oppdatert" : "Kontakt lagt til"); onSaved(); onOpenChange(false); },
    onError: (e) => toastError(e),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] max-w-[94vw]" data-testid="contact-form-sheet">
        <SheetHeader><SheetTitle>{contact ? "Rediger kontakt" : "Ny kontaktperson"}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          <div><Label>Navn *</Label><Input value={f.name} onChange={set("name")} data-testid="contact-name-input" /></div>
          <div><Label>Tittel/rolle</Label><Input value={f.title} onChange={set("title")} /></div>
          <div><Label>E-post</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="contact-email-input" /></div>
          <div><Label>Telefon</Label><Input value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
          <label className="flex items-center justify-between rounded-md border px-3 py-2 text-[13px]"><span>Hovedkontakt</span><Switch checked={f.is_primary} onCheckedChange={(v) => setF({ ...f, is_primary: v })} data-testid="contact-primary-switch" /></label>
          <div><Label>Notater</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} /></div>
          <Button className="w-full" disabled={!f.name || save.isPending} onClick={() => save.mutate()} data-testid="contact-save-button">{save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Lagre</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
